import React from "react";
import styles from "./styles.module.css";
import { formatWithCommasAndDecimals } from "~utils/portfolio/formatNumber";

type PriceRangeBarProps = {
  readonly low: number | null;
  readonly high: number | null;
  readonly current: number | null;
  readonly securityType: string;
};

const TRACK_WIDTH = 84;
const DOT_SIZE = 12;
const DOT_RADIUS = DOT_SIZE / 2;
const USABLE_WIDTH = TRACK_WIDTH - DOT_SIZE;

function getDotLeft(low: number, high: number, current: number): number {
  if (high === low) return DOT_RADIUS;
  const pct = (current - low) / (high - low);
  const clamped = Math.min(Math.max(pct, 0), 1);

  return DOT_RADIUS + clamped * USABLE_WIDTH;
}

function formatPrice(value: number, securityType: string): string {
  return String(
    formatWithCommasAndDecimals(value, securityType === "CUR" ? 4 : 2)
  );
}

function PriceRangeBar({
  low,
  high,
  current,
  securityType,
}: PriceRangeBarProps) {
  if (high === null && low === null)
    return <span className={styles.emDash}>—</span>;

  if (high !== null && low !== null && high === low)
    return <span className={styles.emDash}>—</span>;

  const dotLeft =
    current !== null && low !== null && high !== null && high > low
      ? getDotLeft(low, high, current)
      : null;

  const trackStyle = (
    dotLeft !== null
      ? { "--dot-left": `${dotLeft}px`, "--dot-display": "block" }
      : {}
  ) as React.CSSProperties;

  return (
    <div className={styles.rangeBar}>
      <span className={styles.valueHigh}>
        {high !== null ? formatPrice(high, securityType) : "—"}
      </span>
      <div className={styles.track} style={trackStyle} />
      <span className={styles.valueLow}>
        {low !== null ? formatPrice(low, securityType) : "—"}
      </span>
    </div>
  );
}

export default React.memo(PriceRangeBar);
